import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { notification, Tooltip, Popconfirm } from "antd";
import { PaperClipOutlined, DeleteOutlined, RollbackOutlined, CloseCircleOutlined, EditOutlined } from '@ant-design/icons';
import CustomStore from "devextreme/data/custom_store";

import { IUseTableAllContracts } from "./IUseTableAllContracts";
import { Beneficiaries, ContractData, InvestorDTO, Representative } from "../../Models";

export const useTableAllContracts = ({
	contractAPI,
	signaturitAPI,
	representativesAPI,
	investorsAPI,
	beneficiaryAPI,
	userIdInstitution,
	selectedInstitutionId,
	selectedVehicleId,
	translations,
	moduleRole
}: IUseTableAllContracts) => {
	const navigate = useNavigate();
	const [reloadVersion, setReloadVersion] = useState(0);
	const [isAfinRejectedModalOpen, setIsAfinRejectedModalOpen] = useState<boolean>(false);
	const [afinRejectedMessage, setAfinRejectedMessage] = useState<string>('');
	const isReadOnly = moduleRole === "Read";
	const deletableContractStates = [1, 2, 10, 12, 15, 16, 17];

	const canDeleteContract = (contractState: number) => deletableContractStates.includes(contractState);
	const triggerReload = () => setReloadVersion(current => current + 1);

	const dataSource = useMemo(() => new CustomStore({
		key: "id",
		load: async (loadOptions: any) => {
			const response = await contractAPI.getAllContractsPage({
				skip: loadOptions.skip ?? 0,
				take: loadOptions.take ?? 50,
				sort: loadOptions.sort ? JSON.stringify(loadOptions.sort) : undefined,
				filter: loadOptions.filter ? JSON.stringify(loadOptions.filter) : undefined,
				institutionId: selectedInstitutionId || undefined,
				vehicleId: selectedVehicleId ?? undefined
			});

			if (!response?.success) {
				throw new Error(response?.message || "Unable to load contracts");
			}

			return {
				data: response.data?.data ?? [],
				totalCount: response.data?.totalCount ?? 0
			};
		}
	}), [contractAPI, reloadVersion, selectedInstitutionId, selectedVehicleId]);

	const cancelContract = (id: any) => (
		signaturitAPI.cancelContract(id).then((res: any) => {
			if (res.success) {
				notification.success({ message: 'Contract was canceled' });
				triggerReload();
			} else {
				notification.error({ message: translations.contractNotDeleted });
			}
		})
	);

	const deleteContract = (contractId: number) => (
		contractAPI.deleteContract(contractId).then((res: any) => {
			if (res.success) {
				notification.success({ message: 'Contract was deleted' });
				triggerReload();
			} else {
				notification.error({ message: translations.contractNotDeleted });
			}
		})
	);

	const cellRenderEditIcon = (row: any) => {
		const contractState = row.data.idContractState;
		let editable = false;
		const alwaysEditable = contractState === 1 || contractState === 10 || contractState === 16 || contractState === 17;
		const editableForQualitas = contractState === 3 || contractState === 7 || contractState === 11 || contractState === 13;
		if (userIdInstitution === 536 && (alwaysEditable || editableForQualitas)) editable = true;
		else if (userIdInstitution !== 536 && alwaysEditable) editable = true;

		return (
			<div>
				<Tooltip placement="top" title={isReadOnly ? translations.access : translations.edit}>
					<button className="btnDelete" disabled={!isReadOnly && !editable} onClick={() => editarContrato(row.data.id, row.data.isTransfer)}>
						<EditOutlined type="edit" />
					</button>
				</Tooltip>
			</div>
		);
	};

	const cellRendeDocs = (row: any) => <div>{row.data.totalDocuments}&nbsp;<PaperClipOutlined /></div>;

	const cellRenderCancelIcon = (row: any) => {
		if (isReadOnly) {
			return <></>;
		}

		if (row.data.idContractState === 3) {
			return (
				<Tooltip placement="bottom" title={translations.cancel}>
					<Popconfirm
						title={translations.sureCancelContract}
						onConfirm={() => cancelContract(row.data.id)}
						okText={translations.si}
						cancelText={translations.no}
						placement="left"
					>
						<button className="btnDelete">
							<CloseCircleOutlined />
						</button>
					</Popconfirm>
				</Tooltip>
			);
		} else if (row.data.idContractState === 5) {
			return (
				<Tooltip placement="left" title={'Reactivate'}>
					<Popconfirm
						placement="topLeft"
						title={translations.sureChangeContractState}
						onConfirm={() => rollbackContract(row.data.id)}
						okText={translations.si}
						cancelText={translations.no}
					>
						<RollbackOutlined />
					</Popconfirm>
				</Tooltip>
			);
		} else if (canDeleteContract(row.data.idContractState)) {
			return (
				<Tooltip placement="left" title={translations.delete}>
					<Popconfirm
						title={translations.sureDeleteContract}
						onConfirm={() => deleteContract(row.data.id)}
						okText={translations.si}
						cancelText={translations.no}
						placement="left"
					>
						<button className="btnDelete">
							<DeleteOutlined />
						</button>
					</Popconfirm>
				</Tooltip>
			);
		}

		return <></>;
	};

	const rollbackContract = (contractId: number) => (
		contractAPI.rollbackContract(contractId).then(() => {
			notification.success({ message: 'Contract state has been updated' });
			triggerReload();
		})
	);

	const editarContrato = (contractId: number, isTransfer: boolean) => {
		sessionStorage.isEditing = (!isReadOnly).toString();
		sessionStorage.isCreating = false;
		if (!isTransfer) {
			navigate(`/FormContract?ContractId=${contractId}`);
		} else {
			navigate(`/TransferContract?ContractId=${contractId}`);
		}
	};

	const onOpenAfinRejectedModal = async (contract: ContractData) => {
		let message = `The contract ${contract.id} is in Afin Rejected state. Please review the details and take necessary actions:`;
		const investorResponse = await investorsAPI.getInvestors(contract.id);
		if (investorResponse.success) {
			investorResponse.data?.map((investor: InvestorDTO, index: number) => {
				if (investor.afinStatusId === 3) {
					if (contract.idPersonType === 1) {
						message += `\n- Investor ${index + 1}: ${investor.name} ${investor.lastName} ${investor.secondLastName} is in AFIN Rejected state.`;
					} else {
						message += `\n- Investor ${index + 1}: ${investor.legalForm} is in AFIN Rejected state.`;
					}
				}
				if (investor.launderingRiskId === 5) {
					if (contract.idPersonType === 1) {
						message += `\n- Investor ${index + 1}: ${investor.name} ${investor.lastName} ${investor.secondLastName} has been blocked by AFIN.`;
					} else {
						message += `\n- Investor ${index + 1}: ${investor.legalForm} has been blocked by AFIN.`;
					}
				}
			});
		}
		if (contract.idPersonType === 2) {
			const beneficiaryResponse = await beneficiaryAPI.getBeneficiaries(contract.id);
			if (beneficiaryResponse.success) {
				beneficiaryResponse.data?.map((beneficiary: Beneficiaries, index: number) => {
					if (beneficiary.afinStatusId === 3) {
						message += `\n- Beneficiary ${index + 1}: ${beneficiary.name} ${beneficiary.lastName} is in AFIN Rejected state.`;
					}
					if (beneficiary.launderingRiskId === 5) {
						message += `\n- Beneficiary ${index + 1}: ${beneficiary.name} ${beneficiary.lastName} has been blocked by AFIN.`;
					}
				});
			}
			const representativesResponse = await representativesAPI.getLegalRepresentatives(contract.id);
			if (representativesResponse.success) {
				representativesResponse.data?.map((representative: Representative, index: number) => {
					if (representative.afinStatusId === 3) {
						message += `\n- Representative ${index + 1}: ${representative.name} ${representative.lastName} is in AFIN Rejected state.`;
					}
					if (representative.launderingRiskId === 5) {
						message += `\n- Representative ${index + 1}: ${representative.name} ${representative.lastName} has been blocked by AFIN.`;
					}
				});
			}
		}
		setAfinRejectedMessage(message);
		setIsAfinRejectedModalOpen(true);
	};

	const closeAfinRejectedModal = () => setIsAfinRejectedModalOpen(false);

	return {
		dataSource,
		gridKey: `${selectedInstitutionId || 0}-${selectedVehicleId ?? "all"}-${reloadVersion}`,
		isAfinRejectedModalOpen,
		afinRejectedMessage,
		cellRenderEditIcon,
		cellRendeDocs,
		cellRenderCancelIcon,
		onOpenAfinRejectedModal,
		closeAfinRejectedModal
	};
};
